import { useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Tooltip } from '@radix-ui/themes';
import { Check, ChevronDown, Clock, Lock, RotateCw, Search, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { TONE_CLASSES } from 'components/admin/audit/pillTone';
import type { SectorDesk, ServiceDesk } from 'api/sectorAlignmentApi';
import { buttonLabelState, sourceLabel, withLocked } from './deskSelection';

interface DeskSelectProps {
  desks: ServiceDesk[];
  selected: SectorDesk[];
  analysed: SectorDesk[];
  lockedSd?: SectorDesk | null;
  restored?: boolean;
  busy?: boolean;
  onChange: (sds: SectorDesk[]) => void;
  onAnalyse: () => void;
  onReset: () => void;
}

const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const isActivation = (e: KeyboardEvent) => e.key === 'Enter' || e.key === ' ';
const SKY_DOT = 'h-[6px] w-[6px] shrink-0 rounded-full bg-sky-500 dark:bg-sky-400';

/** §1.1: chips inside a combobox trigger, a 440 px searchable listbox, and the single analyse action (R7). */
const DeskSelect = ({ desks, selected, analysed, lockedSd = null, restored = false, busy = false, onChange, onAnalyse, onReset }: DeskSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const search = useRef<HTMLInputElement>(null);
  const labelId = useId();
  const listId = useId();

  const applicable = desks.filter((d) => d.applicable).map((d) => d.sd);
  const nameOf = (sd: SectorDesk) => desks.find((d) => d.sd === sd)?.name ?? String(sd);
  const visible = query.trim() ? desks.filter((d) => fold(d.name ?? '').includes(fold(query.trim()))) : desks;
  const toggle = (desk: ServiceDesk) => {
    if (!desk.applicable || desk.sd === lockedSd) return;
    onChange(selected.includes(desk.sd) ? selected.filter((sd) => sd !== desk.sd) : [...selected, desk.sd]);
  };
  const checkAll = () => onChange(withLocked(applicable, lockedSd));
  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  };
  const withTip = (key: SectorDesk, content: string, node: ReactNode, side?: 'right' | 'bottom') => (
    <Tooltip key={key} content={content} side={side}>{node}</Tooltip>
  );

  const relaunch = buttonLabelState(selected, analysed) === 'relaunch';

  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-[12.5px] font-semibold">{t('Service desks')}</span>
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-start">
        <Popover.Root open={open} onOpenChange={changeOpen}>
          <Popover.Trigger>
            <div
              role="combobox"
              tabIndex={0}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-controls={listId}
              aria-labelledby={labelId}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget || !(isActivation(e) || e.key === 'ArrowDown')) return;
                e.preventDefault();
                changeOpen(true);
              }}
              className={cn(
                'flex min-h-11 w-full cursor-pointer md:min-h-10 md:w-auto md:min-w-[320px] md:max-w-[760px] md:flex-1 flex-wrap items-center gap-1.5 rounded-[9px] border border-input bg-background px-2 py-1.5 outline-none',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                open && 'ring-2 ring-ring ring-offset-2'
              )}
            >
              {/* §1.8: below md the trigger reads « N desks sélectionnés · Modifier » instead of chips. */}
              <span className="flex flex-1 items-center justify-between px-1 md:hidden">
                <span className="text-[13.5px]">{t('{{count}} desks selected', { count: selected.length })}</span>
                <span className="text-[12.5px] text-muted-foreground underline">{t('Edit')}</span>
              </span>
              {selected.length === 0 && <span className="hidden px-1 text-[13.5px] text-muted-foreground md:inline">{t('Choose service desks…')}</span>}
              {selected.map((sd) =>
                sd === lockedSd ? (
                  withTip(
                    sd,
                    t('Realignment in progress: this desk cannot be removed.'),
                    <span key={sd} tabIndex={0} className={cn('hidden h-[26px] md:inline-flex items-center gap-1.5 rounded-[7px] pl-[9px] pr-[7px] text-[12.5px] font-medium', TONE_CLASSES.sky)}>
                      <span className={cn(SKY_DOT, 'h-[7px] w-[7px]')} aria-hidden="true" />
                      {nameOf(sd)}
                      <span className="text-[11px] font-semibold tracking-[.03em]">{t('in progress')}</span>
                      <Lock className="h-3 w-3" aria-hidden="true" />
                    </span>,
                    'bottom',
                  )
                ) : (
                  <span key={sd} className="hidden h-[26px] md:inline-flex items-center gap-1.5 rounded-[7px] bg-zinc-100 pl-[9px] pr-[7px] text-[12.5px] font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                    {nameOf(sd)}
                    <button
                      type="button"
                      aria-label={t('Remove {{label}}', { label: nameOf(sd) })}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(selected.filter((s) => s !== sd));
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" strokeWidth={2.2} aria-hidden="true" />
                    </button>
                  </span>
                )
              )}
              <ChevronDown className="ml-auto hidden h-[15px] w-[15px] shrink-0 md:block text-muted-foreground" aria-hidden="true" />
            </div>
          </Popover.Trigger>
          <Popover.Content
            align="start"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              search.current?.focus();
            }}
            width="440px"
            maxWidth="calc(100vw - 32px)"
            // Radix Themes floors the popover at the trigger width; §1.1 wants 440 px.
            className="!min-w-0 rounded-[11px] border bg-popover p-1.5 text-popover-foreground shadow-[0_12px_32px_rgba(16,24,40,.16)]"
          >
            <label className="mb-1 flex h-9 items-center gap-2 border-b px-2.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                ref={search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('Search a desk…')}
                aria-label={t('Search a desk…')}
                className="w-full bg-transparent text-[13.5px] outline-none placeholder:text-muted-foreground"
              />
            </label>
            <div id={listId} role="listbox" aria-multiselectable="true" aria-labelledby={labelId} className="max-h-[320px] overflow-y-auto">
              {visible.length === 0 && (
                <p className="px-2.5 py-2 text-[13px] text-muted-foreground">{t('No desk matches “{{query}}”', { query: query.trim() })}</p>
              )}
              {visible.map((desk) => {
                const checked = selected.includes(desk.sd);
                const locked = desk.sd === lockedSd;
                const option = (
                  <div
                    key={desk.sd}
                    role="option"
                    tabIndex={0}
                    aria-selected={checked}
                    aria-disabled={!desk.applicable || locked || undefined}
                    onClick={() => toggle(desk)}
                    onKeyDown={(e) => {
                      if (!isActivation(e)) return;
                      e.preventDefault();
                      toggle(desk);
                    }}
                    className={cn(
                      'group flex h-10 items-center gap-2.5 rounded-[7px] px-2.5 outline-none hover:bg-accent focus-visible:bg-accent',
                      !desk.applicable ? 'cursor-not-allowed opacity-50' : locked ? 'cursor-not-allowed' : 'cursor-pointer'
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        checked ? 'border-primary bg-primary text-primary-foreground' : 'border-zinc-300 bg-background dark:border-zinc-600'
                      )}
                    >
                      {checked && <Check className="h-[11px] w-[11px]" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">{desk.name}</span>
                    {locked && (
                      <span className={cn('inline-flex h-5 items-center gap-1.5 rounded-md px-1.5 text-[11.5px] font-semibold', TONE_CLASSES.sky)}>
                        <span className={SKY_DOT} aria-hidden="true" />
                        {t('in progress')}
                      </span>
                    )}
                    {/* AC22: muted on the accent row falls to 4.34:1 in light. */}
                    <span className="whitespace-nowrap text-[12px] text-muted-foreground group-hover:text-zinc-600 group-focus-visible:text-zinc-600 dark:group-hover:text-zinc-400 dark:group-focus-visible:text-zinc-400">
                      {sourceLabel(t, desk.source)}
                    </span>
                  </div>
                );
                if (!desk.applicable) return withTip(desk.sd, t('This desk has no sector on its tickets.'), option, 'right');
                return locked ? withTip(desk.sd, t('Realignment in progress: this desk cannot be removed.'), option, 'right') : option;
              })}
            </div>
            <div className="flex items-center justify-between px-2.5 pb-1 pt-2 text-[12px] text-muted-foreground">
              <span>{t('{{count}} applicable desks out of {{total}}', { count: applicable.length, total: desks.length })}</span>
              <button type="button" onClick={checkAll} className="font-medium text-foreground underline">
                {t('Check all')}
              </button>
            </div>
          </Popover.Content>
        </Popover.Root>
        <Button className="h-11 w-full rounded-[9px] px-4 text-[13.5px] md:h-10 md:w-auto" onClick={onAnalyse} disabled={busy || selected.length === 0}>
          <RotateCw className={cn('h-[15px] w-[15px]', busy && 'animate-spin')} aria-hidden="true" />
          {relaunch ? t('Relaunch the analysis') : t('Analyze {{count}} desks', { count: selected.length })}
        </Button>
      </div>
      {restored && (
        <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <Clock className="h-3 w-3" aria-hidden="true" />
          {t('Selection remembered from your last visit ·')}
          <button type="button" onClick={onReset} className="underline underline-offset-[3px]">
            {t('Reset')}
          </button>
        </p>
      )}
    </div>
  );
};

export default DeskSelect;
